// web/src/components/auth/ResendVerificationForm.tsx
//
// Used by ConfirmHandler when the email link has expired or never
// arrived — the user has clicked something, ended up at /auth/confirm,
// but we have no way to derive their email (the JWT never landed). A
// small inline form takes the email and re-issues a fresh confirmation
// link via Supabase.
import { useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { CheckCircle2 } from "@/components/icons";
import { FormField } from "./FormField";
import { useResendVerification } from "./useResendVerification";

interface Props {
  defaultEmail?: string;
}

export function ResendVerificationForm({ defaultEmail = "" }: Props) {
  const [email, setEmail] = useState(defaultEmail);
  const { resend, status, cooldownRemaining, error } = useResendVerification();
  const isCoolingDown = cooldownRemaining > 0;
  const disabled = status === "sending" || isCoolingDown || !email;
  const { t } = useTranslation("auth");

  let buttonLabel = t("resend.submit");
  if (status === "sending") buttonLabel = t("resend.sending");
  else if (isCoolingDown) buttonLabel = t("resend.cooldown", { seconds: cooldownRemaining });

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        if (!disabled) resend(email);
      }}
      className="space-y-3"
      aria-label={t("resend.ariaLabel")}
    >
      <FormField
        label={t("resend.email")}
        type="email"
        autoComplete="email"
        required
        value={email}
        onChange={(e) => setEmail(e.target.value)}
      />
      <Button type="submit" disabled={disabled} className="w-full">
        {buttonLabel}
      </Button>
      {status === "sent" && (
        <Alert>
          <CheckCircle2 className="h-4 w-4 text-primary" />
          <AlertDescription>
            <Trans
              i18nKey="auth:resend.success"
              values={{ email }}
              components={{ strong: <strong /> }}
            />
          </AlertDescription>
        </Alert>
      )}
      {status === "error" && error && (
        <p className="text-xs text-destructive" role="alert">
          {error}
        </p>
      )}
    </form>
  );
}
